import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { MapPin, Briefcase, DollarSign, ArrowLeft, Building2, Send, Upload, FileText, CheckCircle, Lock } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CTASection } from '../components/CTASection';
import { ResourceButtons } from '../components/ResourceButtons';
import { formatJobDescription } from '../utils/formatting';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [country, setCountry] = useState('ca');
  const [phone, setPhone] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    country: ''
  });

  // Auto-detect country from IP
  useEffect(() => {
    fetch('https://ipapi.co/json/')
      .then(res => res.json())
      .then(data => {
        if (data?.country_code) {
          setCountry(data.country_code.toLowerCase());
          setFormData(prev => ({ ...prev, country: data.country_name || data.country_code }));
        }
      })
      .catch(() => {
        setCountry('ca');
        setFormData(prev => ({ ...prev, country: 'Canada' }));
      });
  }, []);

  const handleBackClick = () => {
    const embedParam = isEmbed ? '?embed=true' : '';
    navigate(`/jobs${embedParam}`);
  };

  const handleApplyClick = () => {
    setShowApplyForm(true);
    setTimeout(() => {
      document.getElementById('apply-form-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  useEffect(() => {
    fetchJob();
  }, [id]);

  // Add Schema.org JSON-LD to document head
  useEffect(() => {
    if (!job) return;

    const jobSchema = {
      "@context": "https://schema.org/",
      "@type": "JobPosting",
      "title": job.title,
      "description": job.description,
      "identifier": {
        "@type": "PropertyValue",
        "name": job.company,
        "value": job.id
      },
      "datePosted": new Date(job.created_at).toISOString(),
      "validThrough": new Date(new Date(job.created_at).setMonth(new Date(job.created_at).getMonth() + 2)).toISOString(),
      "employmentType": job.job_type.toUpperCase().replace('-', '_'),
      "hiringOrganization": {
        "@type": "Organization",
        "name": job.company,
        "sameAs": window.location.origin
      },
      "jobLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": job.location,
          "addressCountry": "CA"
        }
      },
      "baseSalary": job.salary_min && job.salary_max ? {
        "@type": "MonetaryAmount",
        "currency": "USD",
        "value": {
          "@type": "QuantitativeValue",
          "minValue": job.salary_min,
          "maxValue": job.salary_max,
          "unitText": "YEAR"
        }
      } : undefined,
      "experienceRequirements": {
        "@type": "OccupationalExperienceRequirements",
        "monthsOfExperience": job.experience_level === 'entry' ? 12 : job.experience_level === 'mid' ? 36 : 60
      },
      "skills": job.skills ? job.skills.join(', ') : undefined,
      "qualifications": job.requirements,
      "jobBenefits": job.benefits ? job.benefits.join(', ') : undefined,
      "industry": job.category,
      "url": window.location.href,
      "directApply": true,
      "applicantLocationRequirements": {
        "@type": "Country",
        "name": "Canada"
      }
    };

    Object.keys(jobSchema).forEach(key => jobSchema[key] === undefined && delete jobSchema[key]);

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jobSchema);
    script.id = 'job-schema';
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById('job-schema');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, [job]);

  const fetchJob = async () => {
    try {
      const response = await api.get(`/jobs/${id}`);
      setJob(response.data);
    } catch (error) {
      toast.error('Job not found');
      navigate('/jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a PDF or DOC/DOCX file');
        return;
      }
      setResumeFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.email || !phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!resumeFile) {
      toast.error('Please upload your CV/Resume');
      return;
    }

    setApplying(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        
        const payload = {
          full_name: formData.full_name,
          email: formData.email,
          phone: phone,
          address: formData.country,
          resume_base64: base64,
          resume_filename: resumeFile.name,
          job_id: job.id,
          job_type: 'local',
          job_title: job.title
        };

        try {
          await api.post('/apply-job', payload);
          setApplied(true);
          setShowApplyForm(false);
          toast.success('Application submitted successfully!');
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to submit application');
        } finally {
          setApplying(false);
        }
      };
      reader.readAsDataURL(resumeFile);
    } catch (error) {
      toast.error('Failed to process your application');
      setApplying(false);
    }
  };

  const formatSalary = () => {
    if (!job) return 'Negotiable';
    const min = job.salary_min || 0;
    const max = job.salary_max || 0;
    
    if (min === 0 && max === 0) {
      return 'Negotiable';
    }
    
    const salary = max || min;
    
    if (salary < 1000) {
      const formatHourly = (val) => {
        const formatted = val.toFixed(2);
        return formatted.endsWith('.00') ? val.toString() : formatted;
      };
      
      if (min > 0 && max > 0 && min !== max) {
        return `$${formatHourly(min)}-$${formatHourly(max)}/hr`;
      }
      return `$${formatHourly(salary)}/hr`;
    }
    
    if (min > 0 && max > 0 && min !== max) {
      return `$${(min / 1000).toFixed(0)}k - $${(max / 1000).toFixed(0)}k/yr`;
    }
    return `$${(salary / 1000).toFixed(0)}k/yr`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50">
        <div className="py-32 px-6 flex items-center justify-center" data-testid="loading-state">
          <div className="text-lg text-zinc-500">Loading job details...</div>
        </div>
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="job-detail-page">
      <Helmet>
        <title>{job.title} at {job.company} | Can X Global Careers</title>
        <meta name="description" content={`Apply for ${job.title} at ${job.company} in ${job.location}. ${job.description?.replace(/<[^>]*>/g, '').substring(0, 120)}...`} />
        <meta property="og:title" content={`${job.title} - ${job.company}`} />
        <meta property="og:description" content={`${job.job_type || 'Full-time'} position in ${job.location}. Apply now!`} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`https://canxglobal.com/jobs/${job.id}`} />
      </Helmet>
      
      <main className="pb-32 lg:pb-16 px-4 sm:px-6 pt-6">
        <div className="max-w-5xl mx-auto">
          {/* Back Button */}
          <button
            onClick={handleBackClick}
            className="inline-flex items-center gap-2 text-zinc-600 hover:text-brand-blue transition-colors mb-6 text-sm font-medium"
            data-testid="back-to-jobs-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to All Jobs
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Job Header Card */}
              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-zinc-100">
                <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-4" data-testid="job-title">
                  {job.title}
                </h1>
                
                <div className="flex flex-wrap gap-4 text-sm text-zinc-600 mb-6">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-brand-blue" />
                    <span>{job.company}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-brand-blue" />
                    <span>{job.location}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-brand-blue" />
                    <span className="capitalize">{job.job_type?.replace('-', ' ') || 'Full Time'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-brand-blue" />
                    <span>{formatSalary()}</span>
                  </div>
                </div>

                {/* Apply Button - Desktop */}
                <div className="hidden sm:block">
                  {applied ? (
                    <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-100 text-green-700 rounded-full font-medium">
                      <CheckCircle className="w-5 h-5" />
                      Application Submitted
                    </div>
                  ) : (
                    <Button
                      onClick={handleApplyClick}
                      className="bg-brand-blue hover:bg-brand-blue/90 text-white font-semibold px-8 py-3 rounded-xl shadow-sm transition-all hover:shadow-md"
                      data-testid="apply-now-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Apply for this Position
                    </Button>
                  )}
                </div>
              </div>

              {/* Inline Apply Form */}
              {showApplyForm && !applied && (
                <div id="apply-form-section" className="bg-white rounded-2xl shadow-lg border border-zinc-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-5 text-white">
                    <h2 className="text-xl font-bold">Apply for Position</h2>
                    <p className="text-blue-100 text-sm mt-1">{job.title} • {job.location}</p>
                  </div>
                  
                  <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-zinc-700 mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        placeholder="Enter your full name"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        className="h-12"
                        data-testid="full-name-input"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-zinc-700 mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="email"
                        placeholder="Enter your email address"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="h-12"
                        data-testid="email-input"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-zinc-700 mb-2">
                        Country Currently Living <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        className="h-12 bg-zinc-50"
                        data-testid="country-input"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-zinc-700 mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <PhoneInput
                        country={country}
                        value={phone}
                        onChange={setPhone}
                        inputStyle={{
                          width: '100%',
                          height: '48px',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                          fontSize: '15px'
                        }}
                        containerStyle={{
                          width: '100%'
                        }}
                        buttonStyle={{
                          borderRadius: '8px 0 0 8px',
                          border: '1px solid #e5e7eb',
                          borderRight: 'none'
                        }}
                        specialLabel=""
                        data-testid="phone-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-zinc-700 mb-2">
                        Upload CV / Resume <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                          className="hidden"
                          id="resume-upload"
                          data-testid="resume-upload"
                        />
                        <label
                          htmlFor="resume-upload"
                          className={`flex items-center justify-center gap-3 h-28 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                            resumeFile 
                              ? 'border-green-300 bg-green-50' 
                              : 'border-zinc-200 hover:border-blue-400 hover:bg-blue-50'
                          }`}
                        >
                          {resumeFile ? (
                            <div className="text-center">
                              <FileText className="w-8 h-8 text-green-600 mx-auto mb-2" />
                              <p className="text-sm font-medium text-green-700">{resumeFile.name}</p>
                              <p className="text-xs text-green-600 mt-1">Click to change file</p>
                            </div>
                          ) : (
                            <div className="text-center">
                              <Upload className="w-8 h-8 text-zinc-400 mx-auto mb-2" />
                              <p className="text-sm font-medium text-zinc-600">Click to upload your CV</p>
                              <p className="text-xs text-zinc-400 mt-1">PDF, DOC, DOCX • Max 5MB</p>
                            </div>
                          )}
                        </label>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-14 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-lg font-semibold rounded-xl transition-all hover:shadow-lg"
                      disabled={applying}
                      data-testid="submit-application-btn"
                    >
                      {applying ? 'Submitting...' : 'Apply Now'}
                    </Button>

                    <p className="text-xs text-zinc-500 text-center flex items-center justify-center gap-1">
                      <Lock className="w-3 h-3" />
                      Your information is secure and shared only with the employer.
                    </p>
                  </form>
                </div>
              )}

              {/* Success Message */}
              {applied && (
                <div id="apply-form-section" className="bg-white rounded-2xl p-8 shadow-sm border border-zinc-100 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-zinc-900 mb-2">Application Submitted!</h3>
                  <p className="text-zinc-600 mb-4">
                    Thank you for applying for the {job.title} position.
                  </p>
                  <p className="text-zinc-500 text-sm">
                    We have received your application and will review it shortly. 
                    If your qualifications match our requirements, we will contact you.
                  </p>
                </div>
              )}

              {/* Job Description Card */}
              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-zinc-100">
                <h2 className="text-xl font-bold text-zinc-900 mb-6 pb-4 border-b border-zinc-100">
                  Job Description
                </h2>
                <div 
                  className="job-description"
                  dangerouslySetInnerHTML={{ __html: formatJobDescription(job.description) }}
                />
              </div>

              {job.requirements && job.requirements.trim() && (
                <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-zinc-100">
                  <h2 className="text-xl font-bold text-zinc-900 mb-6 pb-4 border-b border-zinc-100">
                    Requirements
                  </h2>
                  <div 
                    className="job-description"
                    dangerouslySetInnerHTML={{ __html: formatJobDescription(job.requirements) }}
                  />
                </div>
              )}

              {job.benefits && job.benefits.length > 0 && (
                <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-zinc-100">
                  <h2 className="text-xl font-bold text-zinc-900 mb-6 pb-4 border-b border-zinc-100">
                    Benefits
                  </h2>
                  <ul className="space-y-3">
                    {job.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                        <span className="text-zinc-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-zinc-100 lg:sticky lg:top-24">
                <h3 className="font-bold text-zinc-900 mb-5 text-lg">Job Overview</h3>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-cyan-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Briefcase className="w-5 h-5 text-brand-blue" />
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 font-medium uppercase tracking-wide">Job Type</p>
                      <p className="font-semibold text-zinc-800 capitalize">{job.job_type?.replace('-', ' ') || 'Full Time'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-cyan-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-brand-blue" />
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 font-medium uppercase tracking-wide">Location</p>
                      <p className="font-semibold text-zinc-800">{job.location}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-cyan-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <DollarSign className="w-5 h-5 text-brand-blue" />
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 font-medium uppercase tracking-wide">Salary</p>
                      <p className="font-semibold text-zinc-800">{formatSalary()}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-cyan-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-brand-blue" />
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 font-medium uppercase tracking-wide">Experience</p>
                      <p className="font-semibold text-zinc-800 capitalize">{job.experience_level} Level</p>
                    </div>
                  </div>
                </div>

                {/* Sidebar Apply Button */}
                {!applied ? (
                  <Button
                    onClick={handleApplyClick}
                    className="w-full bg-brand-blue hover:bg-brand-blue/90 text-white font-semibold py-3 rounded-xl shadow-sm transition-all hover:shadow-md"
                    data-testid="sidebar-apply-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Apply Now
                  </Button>
                ) : (
                  <div className="flex items-center justify-center gap-2 py-3 bg-green-50 text-green-700 rounded-xl font-medium">
                    <CheckCircle className="w-5 h-5" />
                    Applied
                  </div>
                )}

                {/* Skills Section */}
                {job.skills && job.skills.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-zinc-100">
                    <h3 className="font-bold text-zinc-900 mb-3 text-sm">Skills Required</h3>
                    <div className="flex flex-wrap gap-2">
                      {job.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="px-3 py-1.5 bg-cyan-50 text-brand-blue text-xs font-medium rounded-full"
                          data-testid="skill-tag"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Fixed Mobile Apply Button */}
      {!applied && !showApplyForm && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-zinc-200 shadow-lg lg:hidden z-40">
          <Button
            onClick={handleApplyClick}
            className="w-full py-4 bg-brand-blue hover:bg-brand-blue/90 text-white font-bold text-base rounded-xl shadow-sm"
            data-testid="mobile-apply-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            Apply for this Position
          </Button>
        </div>
      )}
      
      {/* CTA Section */}
      <CTASection />
      
      {/* Resource Section */}
      <ResourceButtons />
    </div>
  );
}
